/**
 * Intro común para detalle de Historia y Conversación: hero + título + descripción
 * + objetivo opcional. Replica la animación de entrada secuencial usada antes
 * por `SituationIntroHeader`, pero acepta los campos disponibles de cualquiera
 * de los dos tipos. La imagen hero se monta vía `useStoryLayoutHero` desde
 * el padre — este componente solo pinta los textos.
 */

import { motion, useReducedMotion } from 'framer-motion';
import {
  storyResourcesEntrance,
  entranceAwareBlockTransition,
} from '@/constants/storyResourcesEntrance';
import { usePageTransitionEntrance } from '@/context/PageTransitionEntranceContext';
import storyStyles from '@/pages/student/StoryPage/StoryPage.module.scss';
import styles from './ContentDetailIntro.module.scss';

export interface ContentDetailIntroProps {
  /** Etiqueta superior (categoría en retos, nivel en conversaciones). */
  eyebrow?: string;
  title: string;
  /** Texto descriptivo principal: `description` en retos, `context` en conversaciones. */
  description?: string;
  /** Objetivo de la situación (conversaciones). El padre lo pasa ya prefijado, p. ej. "Objetivo: …". */
  objective?: string;
}

export function ContentDetailIntro({
  eyebrow,
  title,
  description,
  objective,
}: ContentDetailIntroProps) {
  const pageReady = usePageTransitionEntrance();
  const reduced = Boolean(useReducedMotion());
  const blockY = storyResourcesEntrance.offsetY;
  const hidden = { opacity: 0, y: blockY };
  const shown = { opacity: 1, y: 0 };

  const eyebrowText = eyebrow?.trim();
  const descriptionText = description?.trim();
  const objectiveText = objective?.trim();

  return (
    <div className={storyStyles.story__header}>
      <div className={storyStyles.story__headerInfo}>
        <div className={storyStyles.story__headerTop}>
          {eyebrowText ? (
            <motion.p
              className={storyStyles.story__categoryTag}
              data-story-category=""
              initial={hidden}
              animate={pageReady ? shown : hidden}
              transition={entranceAwareBlockTransition(
                pageReady,
                reduced,
                storyResourcesEntrance.blockDuration,
                storyResourcesEntrance.categoryDelay
              )}
            >
              {eyebrowText}
            </motion.p>
          ) : null}
          <motion.h1
            className={storyStyles.story__title}
            initial={hidden}
            animate={pageReady ? shown : hidden}
            transition={entranceAwareBlockTransition(
              pageReady,
              reduced,
              storyResourcesEntrance.blockDuration,
              storyResourcesEntrance.titleDelay
            )}
          >
            {title}
          </motion.h1>
        </div>
        {descriptionText ? (
          <motion.p
            className={storyStyles.story__description}
            initial={hidden}
            animate={pageReady ? shown : hidden}
            transition={entranceAwareBlockTransition(
              pageReady,
              reduced,
              storyResourcesEntrance.blockDuration,
              storyResourcesEntrance.descriptionDelay
            )}
            style={{ whiteSpace: 'pre-line' }}
          >
            {descriptionText}
          </motion.p>
        ) : null}
        {objectiveText ? (
          <motion.p
            className={storyStyles.story__description}
            initial={hidden}
            animate={pageReady ? shown : hidden}
            transition={entranceAwareBlockTransition(
              pageReady,
              reduced,
              storyResourcesEntrance.blockDuration,
              storyResourcesEntrance.resourcesDelay
            )}
            style={{
              whiteSpace: 'pre-line',
              ...(descriptionText ? { marginTop: '0.75rem' } : {}),
            }}
          >
            {objectiveText}
          </motion.p>
        ) : null}
        {/* Ancla para el fade inferior + reserva de aire bajo el intro:
            cuando este punto está por encima de (viewport - 200px), el degradado
            de StoryLayout se desvanece y deja ver el texto sin recortes. */}
        <div className={styles.introEndAnchor} data-story-intro-end aria-hidden />
      </div>
    </div>
  );
}
